import {
  Button as MuiButton,
  type ButtonOwnProps as MuiButtonOwnProps,
  type PolymorphicProps,
} from '@mui/base'
import { clsx } from 'clsx'
import {
  type ComponentProps,
  type ElementType,
  type JSX,
  type ReactElement,
} from 'react'

import { CircularProgress } from '../CircularProgress'

type IconButtonOwnProps = Omit<
  MuiButtonOwnProps,
  'children' | 'color' | 'disabled' | 'href' | 'rootElementName' | 'to' | 'type'
> & {
  /**
   * The icon to display
   */
  children: ReactElement<ComponentProps<'svg'>, 'svg'>

  /**
   * The color of the component
   * @default primary
   */
  color?: 'primary' | 'secondary'

  /**
   * If `true`, the component is disabled
   * @default false
   */
  disabled?: boolean

  /**
   * If `true`, the loading indicator is visible and the button is disabled
   * @default false
   */
  loading?: boolean

  /**
   * The size of the component. small is equivalent to the dense button styling.
   * @default l
   */
  size?: 'l' | 'm' | 's'

  /**
   * The variant to use
   * @default filled
   */
  variant?: 'filled' | 'ghost'
}

export interface IconButtonTypeMap<
  RootComponentType extends ElementType = 'button',
> {
  defaultComponent: RootComponentType
  props: IconButtonOwnProps
}

export type IconButtonProps<
  RootComponentType extends ElementType = IconButtonTypeMap['defaultComponent'],
> = PolymorphicProps<IconButtonTypeMap<RootComponentType>, RootComponentType>

/**
 * Used to render an icon within a button
 */
export const IconButton = <RootComponentType extends ElementType = 'button'>({
  children,
  className,
  color = 'primary',
  disabled,
  loading = false,
  size = 'l',
  variant = 'filled',
  ...other
}: IconButtonProps<RootComponentType>): JSX.Element => (
  <MuiButton
    className={clsx(
      'icon-button',
      size === 's' && 'icon-button_size-s',
      size === 'm' && 'icon-button_size-m',
      size === 'l' && 'icon-button_size-l',
      color === 'primary' && 'icon-button_color-primary',
      color === 'secondary' && 'icon-button_color-secondary',
      variant === 'filled' && 'icon-button_variant-filled',
      variant === 'ghost' && 'icon-button_variant-ghost',
      className,
    )}
    disabled={loading || disabled}
    {...other}
  >
    {loading ? <CircularProgress /> : children}
  </MuiButton>
)
